import { DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridCellProps } from '@progress/kendo-react-grid';
import { FC, useCallback, useMemo } from 'react';
import GridCellBase from 'src/components/GridCellBase';
import DropDown from 'src/components/design-system/DropDown';
import { InvoiceGlBreakoutItem } from 'src/services/useInvoiceGlBreakoutApi';
import { generateGlCodeLabel } from 'src/services/useInvoiceGlBudgetSummaryApi';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from '../InvoiceDetailsContext';

interface GlBreakoutGlCodeDropDownProps {
  dataItem: InvoiceGlBreakoutItem;
  onChange: (event: DropDownListChangeEvent) => void;
}

const GlBreakoutGlCodeDropDown: FC<GlBreakoutGlCodeDropDownProps> = ({ dataItem, onChange }) => {
  const glCodeOptions = useContextSelector(InvoiceContext, (value) => value.glCodeOptions);
  const throttledInvoiceGLCodes = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceGLCodes
  );

  const existingGlCodesFilteredGlCodeOptions = useMemo(() => {
    const glCodes = throttledInvoiceGLCodes.reduce<string[]>((acc, item) => {
      if (item.glCode) {
        acc.push(item.glCode);
      }
      return acc;
    }, []);
    return glCodeOptions.filter((glCode) => glCode.value && !glCodes.includes(glCode.value));
  }, [glCodeOptions, throttledInvoiceGLCodes]);

  const presentSelectedOption = useMemo(
    () => glCodeOptions.find((item) => item.value === dataItem.glCode),
    [dataItem.glCode, glCodeOptions]
  );

  return (
    <DropDown
      options={[
        ...(presentSelectedOption ? [presentSelectedOption] : []),
        ...existingGlCodesFilteredGlCodeOptions,
      ]}
      onChange={onChange}
      value={dataItem.glCode}
      valid={!!dataItem.glCode}
      popupSettings={{ width: 300 }}
    />
  );
};

const GlCodeDropDown: FC<GridCellProps> = (props) => {
  const setIsGlCodesDirty = useContextSelector(InvoiceContext, (value) => value.setIsGlCodesDirty);
  const glCodesObject = useContextSelector(InvoiceContext, (value) => value.glCodesObject);
  const isInvoiceMPA = useContextSelector(InvoiceContext, (value) => value.isInvoiceMPA);
  const hasUsesInitialBudgetsPermission = useContextSelector(
    InvoiceContext,
    (value) => value.hasUsesInitialBudgetsPermission
  );

  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );

  const { dataItem, dataIndex, onChange, field } = props;
  const { isNewlyAdded } = dataItem;

  const handleChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setIsGlCodesDirty(true);
      onChange?.({
        dataIndex,
        dataItem,
        syntheticEvent: e.syntheticEvent,
        field,
        value: e.value.value,
      });
    },
    [dataIndex, dataItem, field, onChange, setIsGlCodesDirty]
  );

  const display = isInvoiceMPA
    ? glCodesObject[dataItem.glCode as string]?.label
    : generateGlCodeLabel(
        glCodesObject[dataItem.glCode as string]?.label,
        glCodesObject?.[dataItem?.glCode as string],
        hasUsesInitialBudgetsPermission
      );

  return (
    <GridCellBase {...props} title={display}>
      {isNewlyAdded && !isInvoiceEditDisabled ? (
        <GlBreakoutGlCodeDropDown dataItem={dataItem} onChange={handleChange} />
      ) : (
        display
      )}
    </GridCellBase>
  );
};

export default GlCodeDropDown;
